import {
  ApiNoteInProgress,
  usePostByTenantNoteAndNoteIdInstructionApproveMutation,
  usePostByTenantNoteAndNoteIdInstructionRejectMutation
} from 'api/rtk-api'
import { ConfirmUsernameField, LabelValueRow, LinkButton } from 'components/core-v2'
import Icon from 'components/core-v2/atoms/Icon'
import IconButton from 'components/core-v2/molecules/iconButton'
import { intersection } from 'lodash'
import { SyntheticEvent } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useUser } from 'utilities/hooks/useUser'

import { TENANT } from '../../../const'
import { useOnSuccess } from '../utilities/useOnSuccess'
import classes from './activation-tab.module.scss'
import { ConfirmationLink } from './confirmation-link'
import { WaitingOnRoleAlert } from './waiting-on-role-alert'

const AUTHORISED_ROLES = ['Administrator', 'Trading']
const submitOptions = {
  approve: 'submit-approve',
  reject: 'submit-reject'
}
interface Props {
  inProgressNote: ApiNoteInProgress
}
export const ApproveTab = ({ inProgressNote }: Props) => {
  const { data: user } = useUser()
  const { noteId } = useParams()
  const form = useForm()
  const onSuccess = useOnSuccess()

  const [submitApproval, { isLoading }] = usePostByTenantNoteAndNoteIdInstructionApproveMutation()
  const [submitRejection] = usePostByTenantNoteAndNoteIdInstructionRejectMutation()

  if (!user) return null

  const isCurrentStage = inProgressNote.noteStatus === 'PendingApproval'
  const canUserCompleteStep = intersection(user.roles, AUTHORISED_ROLES).length > 0
  const isInteractive = isCurrentStage && canUserCompleteStep

  const onSubmit = async (_, { nativeEvent }: SyntheticEvent<HTMLButtonElement, SubmitEvent>) => {
    const submit =
      nativeEvent.submitter.id === submitOptions.approve ? submitApproval : submitRejection
    const response = await submit({
      tenant: TENANT,
      noteId
    })
    // Success
    if ('data' in response) {
      onSuccess()
    }
  }
  return (
    <FormProvider {...form}>
      {!canUserCompleteStep && isCurrentStage && <WaitingOnRoleAlert />}
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <p>Please download and review the trade confirmations.</p>
        <div className={classes.tradeWrapper}>
          {inProgressNote.associatedTrades.map(trade => (
            <LabelValueRow
              key={trade.accountId}
              label={trade.accountName}
              value={<ConfirmationLink tradeId={trade.accountId} />}
            />
          ))}
        </div>
        {isInteractive && (
          <>
            <ConfirmUsernameField
              id="usernameConfirmation"
              label="To approve the trade, enter your username below and click on the button"
            />
            <div className={classes.buttonWrapper}>
              <IconButton
                icon={<Icon name="check" size="small" />}
                iconPosition="left"
                id={submitOptions.approve}
                isLoading={isLoading}
                operationType="progress"
                text="Approve"
                type="submit"
              />
              <LinkButton id={submitOptions.reject} type="submit">
                Reject
              </LinkButton>
            </div>
          </>
        )}
      </form>
    </FormProvider>
  )
}
